export type Onboarding1AffirmativeChoiceIndex = 0 | 1;

export type Onboarding1ChoiceCorrection = {
	choice: Onboarding1AffirmativeChoiceIndex;
	correction: string;
};

export const normalizeOnboarding1ReviewText = (value: unknown) =>
	String(value ?? '')
		.toLocaleLowerCase('ru-RU')
		.replace(/[.,!?;:()[\]{}«»"'`—–-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

const CHOICE_ORDINALS = [
	['первый', 'первый вариант', 'вариант 1', '1'],
	['второй', 'второй вариант', 'вариант 2', '2']
] as const;

const CHOICE_MARKER_SOURCE =
	'(?:а\\s+)?(?:выбираю|выберу|беру|оставим|ближе|мне\\s+ближе|мне\\s+подходит|подходит|мой\\s+вариант)';
const CHOICE_CLAUSE = new RegExp(`^${CHOICE_MARKER_SOURCE}\\s+(.+)$`, 'i');
const TRAILING_CHOICE_CLAUSE = new RegExp(`(?:^|,)\\s*(${CHOICE_MARKER_SOURCE}\\s+.+)$`, 'i');

const splitReviewClauses = (value: string) =>
	value
		.split(/[.!?;\n]+/)
		.flatMap((sentence) => {
			const trailingChoice = sentence.match(TRAILING_CHOICE_CLAUSE);
			if (!trailingChoice?.[1] || trailingChoice.index === undefined) return [sentence];
			const choiceStart = sentence.indexOf(trailingChoice[1], trailingChoice.index);
			return [sentence.slice(0, choiceStart), trailingChoice[1]];
		})
		.map(normalizeOnboarding1ReviewText)
		.filter(Boolean);

const mentionsChoice = (text: string, index: Onboarding1AffirmativeChoiceIndex, label: string) =>
	CHOICE_ORDINALS[index]
		.filter((ordinal) => ordinal.length > 1)
		.some(
			(ordinal) => text === ordinal || text.includes(`${ordinal} `) || text.includes(` ${ordinal}`)
		) || Boolean(label && (text === label || text.includes(label)));

const hasChoiceNegation = (text: string) =>
	/(?:^|\s)не\s+(?:выбира|бер|остав|подход|ближ|перв|втор)|(?:^|\s)ни\s+(?:перв|втор)|(?:^|\s)не\s+мой\s+вариант|(?:^|\s)не\s+подходит(?:\s|$)/i.test(
		text
	);

const hasChoiceCorrectionCue = (text: string) =>
	/(?:^|\s)(?:сделай|сделайте|измен|исправ|поменя|скоррект|добав|убер|замен)[а-яёa-z-]*(?:\s|$)|(?:^|\s)но(?:\s|$).*(?:короче|мягче|строже|теплее|живее|суше|официальнее|проще)/i.test(
		text
	);

const resolveChoiceClause = (
	clause: string,
	labels: readonly [string, string]
): Onboarding1AffirmativeChoiceIndex | null => {
	const explicitChoice = clause.match(CHOICE_CLAUSE)?.[1];
	if (!explicitChoice) return null;

	for (const index of [0, 1] as const) {
		for (const ordinal of CHOICE_ORDINALS[index]) {
			if (
				explicitChoice === ordinal ||
				(Boolean(labels[index]) &&
					(explicitChoice === labels[index] || explicitChoice === `${ordinal} ${labels[index]}`))
			) {
				return index;
			}
		}
	}
	return null;
};

const choiceTokens = (
	index: Onboarding1AffirmativeChoiceIndex,
	labels: readonly [string, string]
) =>
	[labels[index], ...CHOICE_ORDINALS[index]]
		.map(normalizeOnboarding1ReviewText)
		.filter(Boolean)
		.sort((left, right) => right.length - left.length);

export const resolveOnboarding1ChoiceCorrection = ({
	input,
	labels
}: {
	input: string;
	labels: readonly [string, string];
}): Onboarding1ChoiceCorrection | null => {
	const normalized = normalizeOnboarding1ReviewText(input);
	const normalizedLabels = labels.map(normalizeOnboarding1ReviewText) as [string, string];
	if (!normalized || input.includes('?') || hasChoiceNegation(normalized)) return null;
	const referencedChoices = ([0, 1] as const).filter((index) =>
		mentionsChoice(normalized, index, normalizedLabels[index])
	);
	if (referencedChoices.length !== 1) return null;
	const choice = referencedChoices[0];
	const token = choiceTokens(choice, normalizedLabels).find(
		(candidate) => normalized === candidate || normalized.includes(`${candidate} `)
	);
	if (!token) return null;
	const correction = normalized.slice(normalized.indexOf(token) + token.length).trim();
	if (!correction || !hasChoiceCorrectionCue(correction)) return null;
	return { choice, correction };
};

export const resolveOnboarding1AffirmativeChoice = ({
	input,
	labels
}: {
	input: string;
	labels: readonly [string, string];
}): Onboarding1AffirmativeChoiceIndex | null => {
	const normalized = normalizeOnboarding1ReviewText(input);
	const normalizedLabels = labels.map(normalizeOnboarding1ReviewText) as [string, string];
	if (!normalized) return null;

	if (input.includes('?') || hasChoiceNegation(normalized) || hasChoiceCorrectionCue(normalized)) {
		return null;
	}

	const referencedChoices = ([0, 1] as const).filter((index) =>
		mentionsChoice(normalized, index, normalizedLabels[index])
	);
	if (referencedChoices.length > 1) return null;

	if (normalizedLabels[0] && normalized === normalizedLabels[0]) return 0;
	if (normalizedLabels[1] && normalized === normalizedLabels[1]) return 1;

	const clauses = splitReviewClauses(input);
	const choices = clauses
		.map((clause, index) => ({ choice: resolveChoiceClause(clause, normalizedLabels), index }))
		.filter(
			(entry): entry is { choice: Onboarding1AffirmativeChoiceIndex; index: number } =>
				entry.choice !== null
		);
	if (choices.length !== 1) return null;
	return choices[0].index === clauses.length - 1 ? choices[0].choice : null;
};

export type Onboarding1PresentationField = 'style' | 'delivery';

type Onboarding1PresentationConcept = {
	id: string;
	fields: readonly Onboarding1PresentationField[];
	example: string;
	offeringAnchor: boolean;
	pattern: RegExp;
};

export const ONBOARDING1_PRESENTATION_CONCEPTS = [
	{
		id: 'tone',
		fields: ['style'],
		example: 'тёплый тон',
		offeringAnchor: true,
		pattern: /(?:^|\s)тон[а-яёa-z-]*/i
	},
	{
		id: 'style',
		fields: ['style'],
		example: 'мягкий стиль',
		offeringAnchor: true,
		pattern: /(?:^|\s)стил[а-яёa-z-]*/i
	},
	{
		id: 'voice',
		fields: ['style'],
		example: 'живой голос',
		offeringAnchor: true,
		pattern: /(?:^|\s)голос(?:а|у|ом|е|ов|ами|ах)?(?![а-яёa-z-])/i
	},
	{
		id: 'lexicon',
		fields: ['style'],
		example: 'простую лексику',
		offeringAnchor: true,
		pattern: /(?:^|\s)лексик[а-яёa-z-]*/i
	},
	{
		id: 'emotion',
		fields: ['style'],
		example: 'спокойную эмоциональность',
		offeringAnchor: true,
		pattern: /(?:^|\s)эмоц[а-яёa-z-]*/i
	},
	{
		id: 'sound',
		fields: ['style'],
		example: 'мягкое звучание',
		offeringAnchor: true,
		pattern: /(?:^|\s)звуч[а-яёa-z-]*/i
	},
	{
		id: 'liveliness',
		fields: ['style'],
		example: 'живой голос',
		offeringAnchor: false,
		pattern: /(?:^|\s)жив[а-яёa-z-]*/i
	},
	{
		id: 'warmth',
		fields: ['style'],
		example: 'тёплый тон',
		offeringAnchor: false,
		pattern: /(?:^|\s)т[её]пл[а-яёa-z-]*/i
	},
	{
		id: 'dryness',
		fields: ['style'],
		example: 'сухой стиль',
		offeringAnchor: false,
		pattern: /(?:^|\s)сух[а-яёa-z-]*/i
	},
	{
		id: 'softness',
		fields: ['style'],
		example: 'мягкий тон',
		offeringAnchor: false,
		pattern: /(?:^|\s)мяг[а-яёa-z-]*/i
	},
	{
		id: 'formality',
		fields: ['style'],
		example: 'официальный стиль',
		offeringAnchor: false,
		pattern: /(?:^|\s)официал[а-яёa-z-]*/i
	},
	{
		id: 'friendliness',
		fields: ['style'],
		example: 'дружелюбный голос',
		offeringAnchor: false,
		pattern: /(?:^|\s)дружелюб[а-яёa-z-]*/i
	},
	{
		id: 'first-person',
		fields: ['style'],
		example: 'текст от первого лица',
		offeringAnchor: true,
		pattern: /перв[а-яёa-z-]*\s+лиц[а-яёa-z-]*/i
	},
	{
		id: 'bureaucracy',
		fields: ['style'],
		example: 'текст без канцелярита',
		offeringAnchor: true,
		pattern: /(?:^|\s)канцеляр[а-яёa-z-]*/i
	},
	{
		id: 'wording',
		fields: ['style'],
		example: 'слова «простые»',
		offeringAnchor: true,
		pattern: /слов[а-яёa-z-]*\s+[«"']/i
	},
	{
		id: 'delivery',
		fields: ['delivery'],
		example: 'тёплую подачу',
		offeringAnchor: true,
		pattern: /(?:^|\s)подач[а-яёa-z-]*/i
	},
	{
		id: 'structure',
		fields: ['delivery'],
		example: 'ясную структуру',
		offeringAnchor: true,
		pattern: /(?:^|\s)структур[а-яёa-z-]*/i
	},
	{
		id: 'format',
		fields: ['delivery'],
		example: 'короткий формат',
		offeringAnchor: true,
		pattern: /(?:^|\s)формат[а-яёa-z-]*/i
	},
	{
		id: 'cta',
		fields: ['delivery'],
		example: 'мягкий призыв',
		offeringAnchor: true,
		pattern: /(?:^|\s)(?:призыв[а-яёa-z-]*|cta)(?:\s|$)/i
	},
	{
		id: 'opening',
		fields: ['delivery'],
		example: 'короткий заход',
		offeringAnchor: true,
		pattern: /(?:^|\s)заход[а-яёa-z-]*/i
	},
	{
		id: 'ending',
		fields: ['delivery'],
		example: 'ясное завершение',
		offeringAnchor: true,
		pattern: /(?:^|\s)завершен[а-яёa-z-]*/i
	},
	{
		id: 'rhythm',
		fields: ['delivery'],
		example: 'спокойный ритм',
		offeringAnchor: true,
		pattern: /(?:^|\s)ритм[а-яёa-z-]*/i
	},
	{
		id: 'paragraphs',
		fields: ['delivery'],
		example: 'короткие абзацы',
		offeringAnchor: true,
		pattern: /(?:^|\s)абзац[а-яёa-z-]*/i
	}
] as const satisfies readonly Onboarding1PresentationConcept[];

export const isOnboarding1PresentationCorrectionClause = (
	clause: string,
	field?: Onboarding1PresentationField,
	requireOfferingAnchor = false
) =>
	ONBOARDING1_PRESENTATION_CONCEPTS.some(
		(concept) =>
			(!field || concept.fields.some((conceptField) => conceptField === field)) &&
			(!requireOfferingAnchor || concept.offeringAnchor) &&
			concept.pattern.test(clause)
	);

const EXPLICIT_OFFERING_FIELD =
	/(?:^|\s)(?:услуг[аиу]?|продукт[а-яёa-z-]*|предложени[а-яёa-z-]*)\s*(?:[:—–-]|\b(?:это|на)\b)\s*(.+)$/i;
const EXPLICIT_ACTIVITY_DESCRIPTION =
	/описан[а-яёa-z-]*\s+деятельност[а-яёa-z-]*(?:\s+[а-яёa-z-]+){0,3}\s+на\s+(.+)$/i;
const EXPLICIT_ACTIVITY_QUESTION =
	/(?:что\s+(?:предлага|дела|прода|созда|производ|внедря|печ)[а-яёa-z-]*|чем\s+занима[а-яёa-z-]*)\s*(?:[:—–-]|\b(?:это|на)\b)?\s+(.+)$/i;

type Onboarding1OfferingRelation = {
	kind: 'explicit-field' | 'activity-description' | 'activity-answer' | 'actor-activity';
	source: string;
	value: string;
};

const actorActivityValue = (clause: string) => {
	const normalized = normalizeOnboarding1ReviewText(clause);
	const firstPerson = normalized.match(
		/(?:^|\s)мы\s+(?:занимаемся|прода[её]м|оказываем\s+услуг[иу]?|производим|делаем|созда[её]м|внедряем|печ[её]м|предлагаем)\s+(.+)$/i
	)?.[1];
	if (firstPerson) return firstPerson;

	return normalized.match(
		/(?:^|\s)(?:компания|студия|агентство|фирма|магазин|сервис|[а-яёa-z-]+)\s+(?:занимается|прода[её]т|оказывает\s+услуг[иу]?|производит|делает|созда[её]т|внедряет|печ[её]т|находится|работает)\s+(.+)$/i
	)?.[1];
};

const resolveOnboarding1OfferingRelation = (clause: string): Onboarding1OfferingRelation | null => {
	const explicitFieldValue = clause.match(EXPLICIT_OFFERING_FIELD)?.[1];
	if (explicitFieldValue)
		return { kind: 'explicit-field', source: clause, value: explicitFieldValue };

	const activityDescription = clause.match(EXPLICIT_ACTIVITY_DESCRIPTION)?.[1];
	if (activityDescription) {
		return { kind: 'activity-description', source: clause, value: activityDescription };
	}

	const activityAnswer = clause.match(EXPLICIT_ACTIVITY_QUESTION)?.[1];
	if (activityAnswer) return { kind: 'activity-answer', source: clause, value: activityAnswer };

	const activityValue = actorActivityValue(clause);
	return activityValue ? { kind: 'actor-activity', source: clause, value: activityValue } : null;
};

const hasSubstantiveOfferingRelationValue = ({
	kind,
	source,
	value
}: Onboarding1OfferingRelation) => {
	const normalized = normalizeOnboarding1ReviewText(value);
	if (!normalized) return false;

	// A presentation noun used as the value head is a style/delivery correction,
	// while derived product adjectives such as «голосовой помощник» are not.
	if (isOnboarding1PresentationCorrectionClause(value, undefined, true)) return false;

	// For actor/activity grammar, the subject may itself be the presentation field
	// («Стиль создаёт…»). Explicit offering fields remain authoritative over their labels.
	return (
		kind !== 'actor-activity' || !isOnboarding1PresentationCorrectionClause(source, undefined, true)
	);
};

export const isOnboarding1OfferingCorrectionClause = (clause: string): boolean => {
	const relation = resolveOnboarding1OfferingRelation(clause);
	return Boolean(relation && hasSubstantiveOfferingRelationValue(relation));
};
